import React, { useState } from "react";
import "./App.css";
import { 
  Home, Leaf, ShieldCheck, Zap, Clock, Award, Thermometer,
  Search, FileText, Hammer, CheckCircle, ArrowRight, Star,
  Phone, Mail, MapPin, TrendingDown, TrendingUp, Menu, X
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import MultiStepForm from "./MultiStepForm";
import CookieConsent from "./CookieConsent";
import {
  servizi,
  vantaggi,
  primaDopoData,
  certificazioni,
  processoLavoro,
  testimonianze,
  faqData,
  tipiImmobile,
  tipiIntervento,
  tipologiaImmobile
} from "./mock";

// Icon mapping
const iconMap = {
  home: Home,
  leaf: Leaf,
  "shield-check": ShieldCheck,
  zap: Zap,
  clock: Clock,
  award: Award,
  thermometer: Thermometer,
  search: Search,
  "file-text": FileText,
  hammer: Hammer,
  "check-circle": CheckCircle
};

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    tipoIntervento: "",
    note: ""
  });
  const [calcoloRisparmio, setCalcoloRisparmio] = useState({
    superficie: "",
    costoAnnuale: "",
    primaCasa: true
  });
  const [risparmioCalcolato, setRisparmioCalcolato] = useState(null);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipoIntervento) {
      toast.error("Seleziona il tipo di intervento");
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await axios.post(`${API_URL}/api/contatti/`, formData);
      
      if (response.data) {
        toast.success("✅ Richiesta inviata! Ti contatteremo entro 24 ore.");
        
        setFormData({
          nome: "",
          email: "",
          telefono: "",
          tipoIntervento: "",
          note: ""
        });
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Chiama il Numero Verde: 800.86.46.21");
    }
  };

  const calcolaRisparmio = () => {
    const superficie = parseFloat(calcoloRisparmio.superficie);
    const costoAnnuale = parseFloat(calcoloRisparmio.costoAnnuale);
    const costoIntervento = 3000; // Prezzo base medio
    const primaCasa = calcoloRisparmio.primaCasa;
    
    if (superficie && costoAnnuale) {
      // Risparmio energetico
      const percentualeRisparmio = 35; // Media tra 30-40%
      const risparmioAnnuale = (costoAnnuale * percentualeRisparmio) / 100;
      const risparmioDiecennale = risparmioAnnuale * 10;
      
      // Detrazione fiscale
      const percentualeDetrazione = primaCasa ? 50 : 36;
      const detrazioneMax = costoIntervento * (percentualeDetrazione / 100);
      const detrazioneAnnuale = detrazioneMax / 10; // Ripartita in 10 anni
      
      // Aumento valore immobile (+20% per miglioramento classe energetica)
      const aumentoValore = costoIntervento * 1.2;
      
      // Risparmio totale annuo (bolletta + detrazione)
      const risparmioTotaleAnnuale = risparmioAnnuale + detrazioneAnnuale;
      const risparmioTotale10Anni = risparmioDiecennale + detrazioneMax;
      
      setRisparmioCalcolato({
        risparmioEnergeticoAnnuale: risparmioAnnuale.toFixed(0),
        detrazioneAnnuale: detrazioneAnnuale.toFixed(0),
        detrazioneTotale: detrazioneMax.toFixed(0),
        risparmioTotaleAnnuale: risparmioTotaleAnnuale.toFixed(0),
        risparmioTotale10Anni: risparmioTotale10Anni.toFixed(0),
        aumentoValore: aumentoValore.toFixed(0),
        percentualeRisparmio: percentualeRisparmio,
        percentualeDetrazione: percentualeDetrazione,
        costoIntervento: costoIntervento.toFixed(0)
      });
    }
  };

  const scrollToForm = () => {
    document.getElementById('form-contatti').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="https://customer-assets.emergentagent.com/job_56416778-b2a4-411d-80ed-1aa0e3be8fa0/artifacts/dyq2wthq_REVOGREEN.png" alt="Isoliamo" className="logo-img" />
            </div>
            <nav className={`nav ${menuOpen ? 'nav-open' : ''}`}>
              <a href="#servizi" onClick={() => setMenuOpen(false)}>Servizi</a>
              <a href="#vantaggi" onClick={() => setMenuOpen(false)}>Vantaggi</a>
              <a href="#processo" onClick={() => setMenuOpen(false)}>Come Lavoriamo</a>
              <a href="#testimonianze" onClick={() => setMenuOpen(false)}>Testimonianze</a>
              <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
              <Button onClick={() => { scrollToForm(); setMenuOpen(false); }} className="cta-button">
                Richiedi Preventivo
              </Button>
            </nav>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </header>

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/393791617599?text=Ciao,%20vorrei%20informazioni%20sull'isolamento%20termico" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contattaci su WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="whatsapp-icon">
          <path fill="currentColor" d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-4.713 1.262 1.262-4.669-0.292-0.508c-1.207-2.100-1.847-4.507-1.847-6.924 0-7.435 6.050-13.485 13.485-13.485s13.485 6.050 13.485 13.485c0 7.435-6.050 13.485-13.485 13.485zM21.960 18.735c-0.353-0.177-2.087-1.030-2.410-1.147s-0.558-0.177-0.793 0.177c-0.235 0.353-0.911 1.147-1.117 1.383s-0.411 0.265-0.764 0.088c-0.353-0.177-1.490-0.549-2.838-1.751-1.049-0.935-1.758-2.089-1.964-2.442s-0.022-0.544 0.155-0.719c0.159-0.159 0.353-0.411 0.530-0.617s0.235-0.353 0.353-0.588c0.117-0.235 0.058-0.441-0.029-0.617s-0.793-1.911-1.087-2.616c-0.287-0.688-0.578-0.595-0.793-0.605-0.206-0.010-0.441-0.012-0.676-0.012s-0.617 0.088-0.940 0.441c-0.323 0.353-1.234 1.206-1.234 2.942s1.263 3.413 1.440 3.648c0.177 0.235 2.489 3.799 6.031 5.331 0.842 0.365 1.500 0.583 2.012 0.747 0.847 0.267 1.617 0.229 2.227 0.139 0.679-0.102 2.087-0.853 2.381-1.677s0.294-1.529 0.206-1.677c-0.088-0.147-0.323-0.235-0.676-0.411z"/>
        </svg>
      </a>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Risparmia fino al <span className="highlight">40%</span> sulla Bolletta
              </h1>
              <p className="hero-subtitle">
                Isolamento termico ecologico con fibra di cellulosa per sottotetti e intercapedini. 
                Più comfort, meno spese, casa valorizzata.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <CheckCircle className="icon-check" />
                  <span>Sopralluogo gratuito</span>
                </div>
                <div className="hero-feature">
                  <CheckCircle className="icon-check" />
                  <span>Bonus 50% prima casa</span>
                </div>
                <div className="hero-feature">
                  <CheckCircle className="icon-check" />
                  <span>Garanzia Isoliamo</span>
                </div>
              </div>
              <div className="hero-contact">
                <div className="contact-badge">
                  <Phone size={20} />
                  <div>
                    <span className="label">Numero Verde</span>
                    <a href="tel:800864621" className="value">800.86.46.21</a>
                  </div>
                </div>
                <div className="contact-badge whatsapp-badge">
                  <svg viewBox="0 0 32 32" className="whatsapp-small">
                    <path fill="currentColor" d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-4.713 1.262 1.262-4.669-0.292-0.508c-1.207-2.100-1.847-4.507-1.847-6.924 0-7.435 6.050-13.485 13.485-13.485s13.485 6.050 13.485 13.485c0 7.435-6.050 13.485-13.485-13.485zM21.960 18.735c-0.353-0.177-2.087-1.030-2.410-1.147s-0.558-0.177-0.793 0.177c-0.235 0.353-0.911 1.147-1.117 1.383s-0.411 0.265-0.764 0.088c-0.353-0.177-1.490-0.549-2.838-1.751-1.049-0.935-1.758-2.089-1.964-2.442s-0.022-0.544 0.155-0.719c0.159-0.159 0.353-0.411 0.530-0.617s0.235-0.353 0.353-0.588c0.117-0.235 0.058-0.441-0.029-0.617s-0.793-1.911-1.087-2.616c-0.287-0.688-0.578-0.595-0.793-0.605-0.206-0.010-0.441-0.012-0.676-0.012s-0.617 0.088-0.940 0.441c-0.323 0.353-1.234 1.206-1.234 2.942s1.263 3.413 1.440 3.648c0.177 0.235 2.489 3.799 6.031 5.331 0.842 0.365 1.500 0.583 2.012 0.747 0.847 0.267 1.617 0.229 2.227 0.139 0.679-0.102 2.087-0.853 2.381-1.677s0.294-1.529 0.206-1.677c-0.088-0.147-0.323-0.235-0.676-0.411z"/>
                  </svg>
                  <div>
                    <span className="label">WhatsApp</span>
                    <a href="https://wa.me/393791617599" className="value">379 161 7599</a>
                  </div>
                </div>
              </div>
              <div className="hero-cta">
                <Button size="lg" className="cta-primary" onClick={scrollToForm}>
                  Richiedi Preventivo Gratuito
                  <ArrowRight className="ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="cta-secondary" onClick={() => document.getElementById('vantaggi').scrollIntoView({ behavior: 'smooth' })}>
                  Scopri i Vantaggi
                </Button>
              </div>
            </div>
            <div className="hero-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_thermal-comfort-3/artifacts/398pjr8n_ChatGPT%20Image%2017%20feb%202026%2C%2008_14_53%20%281%29.png" 
                alt="Isolamento con fibra di cellulosa grigia Isoliamo"
                className="hero-img"
              />
              <div className="hero-stats">
                <div className="stat-card">
                  <TrendingDown className="stat-icon" />
                  <div className="stat-value">-40%</div>
                  <div className="stat-label">Costi Energetici</div>
                </div>
                <div className="stat-card">
                  <TrendingUp className="stat-icon" />
                  <div className="stat-value">+30%</div>
                  <div className="stat-label">Valore Casa</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section id="servizi" className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">I Nostri Servizi</h2>
            <p className="section-subtitle">Soluzioni professionali per ogni esigenza</p>
          </div>
          <div className="servizi-grid">
            {servizi.map(servizio => {
              const IconComponent = iconMap[servizio.icon] || Home;
              return (
                <Card key={servizio.id} className="servizio-card">
                  <CardHeader>
                    <div className="servizio-icon">
                      <IconComponent size={32} />
                    </div>
                    <CardTitle>{servizio.title}</CardTitle>
                    <CardDescription>{servizio.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="feature-list">
                      {servizio.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle size={16} className="feature-check" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vantaggi */}
      <section id="vantaggi" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perché Scegliere Isoliamo</h2>
            <p className="section-subtitle">I vantaggi della fibra di cellulosa</p>
          </div>
          <div className="vantaggi-grid">
            {vantaggi.map(vantaggio => {
              const IconComponent = iconMap[vantaggio.icon] || CheckCircle;
              return (
                <div key={vantaggio.id} className="vantaggio-item">
                  <div className="vantaggio-icon">
                    <IconComponent size={28} />
                  </div>
                  <h3 className="vantaggio-title">{vantaggio.title}</h3>
                  <p className="vantaggio-description">{vantaggio.description}</p>
                </div>
              );
            })}
          </div>
          
        </div>
      </section>

      {/* Perché Isolare la Casa */}
      <section className="section section-perche">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perché Isolare la Casa?</h2>
            <p className="section-subtitle">Le dispersioni termiche stanno svuotando il tuo portafoglio</p>
          </div>
          
          <div className="dispersioni-content">
            <div className="dispersioni-text">
              <h3>Dove Si Perde il Calore (e i Tuoi Soldi)</h3>
              <p className="dispersioni-intro">
                Le maggiori dispersioni di un edificio avvengono attraverso le superfici dell'involucro: 
                tetto, pareti esterne, pavimenti, serramenti. <strong>Isolare il tetto può ridurre 
                fino al 60% delle dispersioni totali!</strong>
              </p>
              
              <div className="dispersioni-grid">
                <div className="dispersione-item critical">
                  <div className="dispersione-icon">🏠</div>
                  <div className="dispersione-info">
                    <h4>Tetto</h4>
                    <div className="dispersione-bar">
                      <div className="dispersione-fill" style={{width: '50%', background: '#dc2626'}}></div>
                    </div>
                    <span className="dispersione-value">40-60%</span>
                    <p>La maggior dispersione! Il calore sale e si disperde attraverso il tetto</p>
                  </div>
                </div>
                
                <div className="dispersione-item high">
                  <div className="dispersione-icon">🧱</div>
                  <div className="dispersione-info">
                    <h4>Pareti</h4>
                    <div className="dispersione-bar">
                      <div className="dispersione-fill" style={{width: '20%', background: '#ea580c'}}></div>
                    </div>
                    <span className="dispersione-value">15-25%</span>
                    <p>Trasferiscono calore verso l'esterno, specialmente se non isolate</p>
                  </div>
                </div>
                
                <div className="dispersione-item medium">
                  <div className="dispersione-icon">🪟</div>
                  <div className="dispersione-info">
                    <h4>Altro (Finestre/Altro)</h4>
                    <div className="dispersione-bar">
                      <div className="dispersione-fill" style={{width: '25%', background: '#f59e0b'}}></div>
                    </div>
                    <span className="dispersione-value">25%</span>
                    <p>Serramenti e altri punti di dispersione minori</p>
                  </div>
                </div>
                
                <div className="dispersione-item low">
                  <div className="dispersione-icon">⬇️</div>
                  <div className="dispersione-info">
                    <h4>Pavimento</h4>
                    <div className="dispersione-bar">
                      <div className="dispersione-fill" style={{width: '15%', background: '#22c55e'}}></div>
                    </div>
                    <span className="dispersione-value">10-20%</span>
                    <p>Dispersione verso il terreno o ambienti non climatizzati</p>
                  </div>
                </div>
              </div>
              
              <div className="cta-urgency">
                <div className="urgency-content">
                  <h4>⚡ Ogni giorno senza isolamento = Soldi buttati!</h4>
                  <p>Una casa di 100mq non isolata può sprecare fino a <strong>€800-1.200 all'anno</strong> in energia dispersa.</p>
                  <Button size="lg" className="cta-urgency-btn" onClick={scrollToForm}>
                    Richiedi Sopralluogo Gratuito Ora
                    <ArrowRight className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="dispersioni-visual">
              <img 
                src="https://customer-assets.emergentagent.com/job_thermal-comfort-3/artifacts/y8jv2ahw_isolamento-cappotto-termico-o-fibra-cellulosa.jpg" 
                alt="Isolamento con fibra di cellulosa grigia"
                className="infografica-img"
              />
              <div className="trust-badge">
                <CheckCircle size={24} />
                <span>Isolando tetto + pareti riduci fino all'<strong>80%</strong> le dispersioni!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prima/Dopo */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-white">Prima e Dopo l'Isolamento</h2>
            <p className="section-subtitle text-white-70">Risultati concreti e misurabili</p>
          </div>
          <div className="prima-dopo-grid">
            {primaDopoData.map(item => (
              <Card key={item.id} className="prima-dopo-card">
                <CardHeader>
                  <CardTitle className="text-center">{item.categoria}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="comparison">
                    <div className="comparison-item before">
                      <span className="label">Prima</span>
                      <span className="value">{item.prima}</span>
                    </div>
                    <div className="arrow">→</div>
                    <div className="comparison-item after">
                      <span className="label">Dopo</span>
                      <span className="value">{item.dopo}</span>
                    </div>
                  </div>
                  <div className="improvement">
                    <TrendingUp size={20} />
                    <span>Miglioramento: {item.miglioramento}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certificazioni */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Certificazioni e Garanzie</h2>
            <p className="section-subtitle">Qualità certificata, sicurezza garantita</p>
          </div>
          <div className="certificazioni-grid">
            {certificazioni.map(cert => (
              <div key={cert.id} className="certificazione-item">
                <ShieldCheck className="cert-icon" />
                <h3>{cert.nome}</h3>
                <p>{cert.descrizione}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pratica ENEA */}
      <section className="section section-enea">
        <div className="container">
          <div className="enea-content">
            <div className="enea-text">
              <h2 className="section-title">Pratica ENEA? Ci Pensiamo Noi!</h2>
              <p className="enea-description">
                Per accedere alle detrazioni fiscali del <strong>Bonus Ristrutturazione</strong> è necessario 
                completare la pratica ENEA entro 90 giorni dalla fine lavori.
              </p>
              <div className="enea-benefits">
                <div className="enea-benefit">
                  <CheckCircle size={24} className="enea-icon" />
                  <div>
                    <h4>Gestione Completa</h4>
                    <p>Ci occupiamo di tutta la documentazione tecnica e amministrativa</p>
                  </div>
                </div>
                <div className="enea-benefit">
                  <CheckCircle size={24} className="enea-icon" />
                  <div>
                    <h4>Invio Telematico</h4>
                    <p>Trasmettiamo la pratica all'ENEA nei tempi previsti</p>
                  </div>
                </div>
                <div className="enea-benefit">
                  <CheckCircle size={24} className="enea-icon" />
                  <div>
                    <h4>Certificazioni Incluse</h4>
                    <p>APE, asseverazioni e tutti i documenti necessari</p>
                  </div>
                </div>
              </div>
              <div className="enea-highlight">
                <FileText size={28} />
                <p>
                  <strong>Servizio incluso nel prezzo!</strong><br />
                  Non devi preoccuparti di nulla, pensiamo a tutto noi per farti ottenere 
                  la detrazione fiscale del 50% (prima casa) o 36% (altre abitazioni).
                </p>
              </div>
            </div>
            <div className="enea-image">
              <div className="enea-card">
                <h3>Detrazioni Fiscali 2024</h3>
                <div className="detrazione-item">
                  <div className="detrazione-label">Prima Casa</div>
                  <div className="detrazione-value">50%</div>
                  <div className="detrazione-desc">Ripartita in 10 anni</div>
                </div>
                <div className="detrazione-separator"></div>
                <div className="detrazione-item">
                  <div className="detrazione-label">Altre Abitazioni</div>
                  <div className="detrazione-value">36%</div>
                  <div className="detrazione-desc">Ripartita in 10 anni</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Processo di Lavoro */}
      <section id="processo" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Come Lavoriamo</h2>
            <p className="section-subtitle">4 semplici passaggi verso il comfort</p>
          </div>
          <div className="processo-timeline">
            {processoLavoro.map(step => {
              const IconComponent = iconMap[step.icon] || CheckCircle;
              return (
                <div key={step.step} className="processo-step">
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              );
            })}
          </div>
          
          {/* Infografiche tecniche */}
          <div className="tecniche-grid">
            <div className="tecnica-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_thermal-comfort-3/artifacts/eu7w55d0_IMG_4485.jpeg" 
                alt="Fibra di cellulosa - Materiale isolante ecologico"
                className="tecnica-img"
              />
              <div className="tecnica-overlay">
                <h4>Fibra di Cellulosa</h4>
                <p>Materiale isolante naturale ed ecologico</p>
              </div>
            </div>
            <div className="tecnica-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_thermal-comfort-3/artifacts/p31phlxs_IMG_4486.jpeg" 
                alt="Tecnica insufflaggio - Isolamento professionale"
                className="tecnica-img"
              />
              <div className="tecnica-overlay">
                <h4>Tecnica Insufflaggio</h4>
                <p>Installazione rapida e professionale</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calcolo Risparmio */}
      <section className="section section-accent">
        <div className="container">
          <div className="calcolo-risparmio">
            <div className="calcolo-header">
              <h2 className="section-title">Calcola il Tuo Risparmio Totale</h2>
              <p className="section-subtitle">Risparmio energetico + Detrazioni fiscali + Valore immobile</p>
              <p className="calcolo-note">Calcolato su investimento medio di €3.000</p>
            </div>
            <Card className="calcolo-card">
              <CardContent className="calcolo-content">
                <div className="calcolo-inputs-grid">
                  <div className="input-group">
                    <Label htmlFor="calc-superficie">Superficie da isolare (mq)</Label>
                    <Input
                      id="calc-superficie"
                      type="number"
                      placeholder="Es: 100"
                      value={calcoloRisparmio.superficie}
                      onChange={(e) => setCalcoloRisparmio(prev => ({ ...prev, superficie: e.target.value }))}
                    />
                  </div>
                  <div className="input-group">
                    <Label htmlFor="calc-costo">Costo annuale riscaldamento (€)</Label>
                    <Input
                      id="calc-costo"
                      type="number"
                      placeholder="Es: 2000"
                      value={calcoloRisparmio.costoAnnuale}
                      onChange={(e) => setCalcoloRisparmio(prev => ({ ...prev, costoAnnuale: e.target.value }))}
                    />
                  </div>
                  <div className="input-group checkbox-group">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="prima-casa"
                        checked={calcoloRisparmio.primaCasa}
                        onChange={(e) => setCalcoloRisparmio(prev => ({ ...prev, primaCasa: e.target.checked }))}
                        className="checkbox-input"
                      />
                      <Label htmlFor="prima-casa" className="checkbox-label">
                        È prima casa (50% detrazione)
                      </Label>
                    </div>
                  </div>
                </div>
                <Button onClick={calcolaRisparmio} className="calcola-btn-full">
                  Calcola Risparmio Totale
                </Button>
                {risparmioCalcolato && (
                  <div className="risultato-completo">
                    <div className="risultato-grid">
                      <div className="risultato-card primary">
                        <Zap className="risultato-icon" />
                        <span className="risultato-label">Risparmio Energetico Annuale</span>
                        <span className="risultato-value">€{risparmioCalcolato.risparmioEnergeticoAnnuale}</span>
                        <span className="risultato-note">-{risparmioCalcolato.percentualeRisparmio}% sulla bolletta</span>
                      </div>
                      <div className="risultato-card secondary">
                        <Award className="risultato-icon" />
                        <span className="risultato-label">Detrazione Fiscale Annuale</span>
                        <span className="risultato-value">€{risparmioCalcolato.detrazioneAnnuale}</span>
                        <span className="risultato-note">{risparmioCalcolato.percentualeDetrazione}% in 10 anni</span>
                      </div>
                      <div className="risultato-card success">
                        <TrendingUp className="risultato-icon" />
                        <span className="risultato-label">Risparmio Totale Annuale</span>
                        <span className="risultato-value big">€{risparmioCalcolato.risparmioTotaleAnnuale}</span>
                        <span className="risultato-note">Bolletta + Detrazione</span>
                      </div>
                    </div>
                    <div className="risultato-summary">
                      <div className="summary-item">
                        <span className="summary-label">💰 Risparmio totale in 10 anni:</span>
                        <span className="summary-value highlight">€{risparmioCalcolato.risparmioTotale10Anni}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">🏠 Aumento valore immobile:</span>
                        <span className="summary-value">€{risparmioCalcolato.aumentoValore}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">📋 Detrazione fiscale totale:</span>
                        <span className="summary-value">€{risparmioCalcolato.detrazioneTotale}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonianze */}
      <section id="testimonianze" className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Cosa Dicono i Nostri Clienti</h2>
            <p className="section-subtitle">Oltre 1000 famiglie soddisfatte in Veneto e Lombardia</p>
          </div>
          
          <div className="social-proof-banner">
            <div className="proof-item">
              <Star fill="#FFB800" color="#FFB800" size={28} />
              <div>
                <strong>4.9/5</strong>
                <span>Rating medio</span>
              </div>
            </div>
            <div className="proof-item">
              <CheckCircle size={28} color="#16a34a" />
              <div>
                <strong>1000+</strong>
                <span>Case isolate</span>
              </div>
            </div>
            <div className="proof-item">
              <Award size={28} color="#004B9D" />
              <div>
                <strong>15 anni</strong>
                <span>Di esperienza</span>
              </div>
            </div>
            <div className="proof-item">
              <TrendingUp size={28} color="#16a34a" />
              <div>
                <strong>40%</strong>
                <span>Risparmio medio</span>
              </div>
            </div>
          </div>
          <div className="testimonianze-grid">
            {testimonianze.map(test => (
              <Card key={test.id} className="testimonianza-card">
                <CardContent className="testimonianza-content">
                  <div className="rating">
                    {[...Array(test.rating)].map((_, i) => (
                      <Star key={i} size={18} fill="#FFB800" color="#FFB800" />
                    ))}
                  </div>
                  <p className="testimonianza-text">"{test.testo}"</p>
                  <div className="testimonianza-author">
                    <div>
                      <strong>{test.nome}</strong>
                      <span className="citta">{test.citta}</span>
                    </div>
                    <span className="servizio-badge">{test.servizio}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Domande Frequenti</h2>
            <p className="section-subtitle">Tutto quello che devi sapere</p>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqData.map(faq => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.domanda}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.risposta}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Form Contatti */}
      <section id="form-contatti" className="section section-cta">
        <div className="container">
          <div className="form-section-header">
            <h2 className="section-title text-white">Richiedi il Tuo Preventivo Gratuito</h2>
            <p className="section-subtitle text-white-70">
              Compila il form in 3 semplici step. Il sopralluogo è sempre gratuito e senza impegno.
            </p>
            <div className="contact-info-inline">
              <div className="contact-item">
                <Phone size={20} />
                <a href="tel:800864621" className="contact-link">Numero Verde: 800.86.46.21</a>
              </div>
              <div className="contact-item">
                <svg viewBox="0 0 32 32" width="20" height="20" fill="currentColor">
                  <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-4.713 1.262 1.262-4.669-0.292-0.508c-1.207-2.100-1.847-4.507-1.847-6.924 0-7.435 6.050-13.485 13.485-13.485s13.485 6.050 13.485 13.485c0 7.435-6.050 13.485-13.485 13.485zM21.960 18.735c-0.353-0.177-2.087-1.030-2.410-1.147s-0.558-0.177-0.793 0.177c-0.235 0.353-0.911 1.147-1.117 1.383s-0.411 0.265-0.764 0.088c-0.353-0.177-1.490-0.549-2.838-1.751-1.049-0.935-1.758-2.089-1.964-2.442s-0.022-0.544 0.155-0.719c0.159-0.159 0.353-0.411 0.530-0.617s0.235-0.353 0.353-0.588c0.117-0.235 0.058-0.441-0.029-0.617s-0.793-1.911-1.087-2.616c-0.287-0.688-0.578-0.595-0.793-0.605-0.206-0.010-0.441-0.012-0.676-0.012s-0.617 0.088-0.940 0.441c-0.323 0.353-1.234 1.206-1.234 2.942s1.263 3.413 1.440 3.648c0.177 0.235 2.489 3.799 6.031 5.331 0.842 0.365 1.500 0.583 2.012 0.747 0.847 0.267 1.617 0.229 2.227 0.139 0.679-0.102 2.087-0.853 2.381-1.677s0.294-1.529 0.206-1.677c-0.088-0.147-0.323-0.235-0.676-0.411z"/>
                </svg>
                <a href="https://wa.me/393791617599" className="contact-link">WhatsApp: 379 161 7599</a>
              </div>
              <div className="contact-item">
                <MapPin size={20} />
                <span>Via Roma 44B, Mozzecane (VR)</span>
              </div>
            </div>
          </div>
          <MultiStepForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img src="https://customer-assets.emergentagent.com/job_56416778-b2a4-411d-80ed-1aa0e3be8fa0/artifacts/dyq2wthq_REVOGREEN.png" alt="Isoliamo" className="footer-logo" />
              <p className="footer-tagline">Energia per la tua casa</p>
              <p className="footer-company">Marchio di Revogreen Srl</p>
              <p className="footer-piva">P.IVA 05121010233</p>
            </div>
            <div className="footer-section">
              <h4>Contatti</h4>
              <div className="footer-contact">
                <Phone size={16} />
                <a href="tel:800864621">Numero Verde: 800.86.46.21</a>
              </div>
              <div className="footer-contact">
                <svg viewBox="0 0 32 32" width="16" height="16" fill="currentColor">
                  <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-4.713 1.262 1.262-4.669-0.292-0.508c-1.207-2.100-1.847-4.507-1.847-6.924 0-7.435 6.050-13.485 13.485-13.485s13.485 6.050 13.485 13.485c0 7.435-6.050 13.485-13.485 13.485zM21.960 18.735c-0.353-0.177-2.087-1.030-2.410-1.147s-0.558-0.177-0.793 0.177c-0.235 0.353-0.911 1.147-1.117 1.383s-0.411 0.265-0.764 0.088c-0.353-0.177-1.490-0.549-2.838-1.751-1.049-0.935-1.758-2.089-1.964-2.442s-0.022-0.544 0.155-0.719c0.159-0.159 0.353-0.411 0.530-0.617s0.235-0.353 0.353-0.588c0.117-0.235 0.058-0.441-0.029-0.617s-0.793-1.911-1.087-2.616c-0.287-0.688-0.578-0.595-0.793-0.605-0.206-0.010-0.441-0.012-0.676-0.012s-0.617 0.088-0.940 0.441c-0.323 0.353-1.234 1.206-1.234 2.942s1.263 3.413 1.440 3.648c0.177 0.235 2.489 3.799 6.031 5.331 0.842 0.365 1.500 0.583 2.012 0.747 0.847 0.267 1.617 0.229 2.227 0.139 0.679-0.102 2.087-0.853 2.381-1.677s0.294-1.529 0.206-1.677c-0.088-0.147-0.323-0.235-0.676-0.411z"/>
                </svg>
                <a href="https://wa.me/393791617599">WhatsApp: 379 161 7599</a>
              </div>
              <div className="footer-contact">
                <MapPin size={16} />
                <span>Via Roma 44B, Mozzecane (VR)</span>
              </div>
            </div>
            <div className="footer-section">
              <h4>Link Utili</h4>
              <ul className="footer-links">
                <li><a href="#servizi">Servizi</a></li>
                <li><a href="#vantaggi">Vantaggi</a></li>
                <li><a href="#testimonianze">Testimonianze</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Revogreen</h4>
              <ul className="footer-links">
                <li><a href="https://revogreenitalia.com" target="_blank" rel="noopener noreferrer">Sito Principale</a></li>
                <li><a href="#chi-siamo">Chi Siamo</a></li>
                <li><a href="#privacy">Privacy Policy</a></li>
                <li><a href="#cookie">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Isoliamo - Revogreen Srl. Tutti i diritti riservati. P.IVA 05121010233</p>
          </div>
        </div>
      </footer>
      
      {/* Cookie Consent Banner GDPR */}
      <CookieConsent />
    </div>
  );
}

export default App;
